import { useMemo, useState } from 'react'

import { Badge, EmptyState, ErrorState, FilteredEmptyState, LoadingState } from '../components/States'
import { JsonDisclosure } from '../components/JsonDisclosure'
import type { Exchange } from '../lib/apiTypes'
import { formatCount, formatDateTimeWib, formatDuration } from '../lib/format'
import { useRejections } from '../lib/hooks'
import { verificationLabel } from '../lib/status'

/**
 * Notifikasi bank yang ditolak karena gagal verifikasi. Daftar ini yang dipakai
 * operator untuk membedakan masalah kredensial dari masalah jaringan: keduanya
 * terlihat sama dari sisi transaksi, tetapi hanya di sini penyebabnya terlihat.
 */
export function InboundRejectionsPage() {
  const [credential, setCredential] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [extra, setExtra] = useState<Exchange[]>([])

  const query = useMemo(
    () => ({
      credential: credential || undefined,
      from: from || undefined,
      to: to || undefined,
      cursor,
    }),
    [credential, from, to, cursor],
  )

  const rejections = useRejections(query)
  const rows = cursor ? mergeRows(extra, rejections.data?.data ?? []) : (rejections.data?.data ?? [])
  const filtered = Boolean(credential || from || to)

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>Notifikasi ditolak</h1>
          <p className="muted">
            Permintaan masuk dari bank yang gagal verifikasi tanda tangan atau token,
            termasuk yang hanya dicatat karena verifikasi dilewati di lingkungan
            pengembangan. Isi rahasia sudah disamarkan saat dicatat.
          </p>
        </div>
      </div>

      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault()
          setCursor(undefined)
          setExtra([])
        }}
      >
        <div className="form-grid">
          <div className="field">
            <label className="field__label" htmlFor="rej-credential">
              Kredensial
            </label>
            <input
              id="rej-credential"
              className="input"
              type="search"
              placeholder="slug kredensial"
              value={credential}
              onChange={(event) => setCredential(event.target.value)}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="rej-from">
              Dari tanggal
            </label>
            <input
              id="rej-from"
              className="input"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="rej-to">
              Sampai tanggal
            </label>
            <input
              id="rej-to"
              className="input"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
        </div>
        <div className="toolbar">
          <div className="toolbar__actions">
            <button type="submit" className="btn btn--primary">
              Terapkan
            </button>
            <button
              type="button"
              className="btn"
              disabled={!filtered}
              onClick={() => {
                setCredential('')
                setFrom('')
                setTo('')
                setCursor(undefined)
                setExtra([])
              }}
            >
              Bersihkan
            </button>
          </div>
        </div>
      </form>

      {rejections.isPending ? (
        <LoadingState what="daftar notifikasi ditolak" />
      ) : rejections.isError ? (
        <ErrorState
          title="Gagal memuat notifikasi ditolak"
          error={rejections.error}
          onRetry={() => void rejections.refetch()}
          retrying={rejections.isFetching}
        />
      ) : rows.length === 0 ? (
        filtered ? (
          <FilteredEmptyState
            what="notifikasi ditolak"
            onClear={() => {
              setCredential('')
              setFrom('')
              setTo('')
              setCursor(undefined)
              setExtra([])
            }}
          />
        ) : (
          <EmptyState
            title="Tidak ada notifikasi yang ditolak"
            body="Semua permintaan masuk dari bank lolos verifikasi. Baris muncul di sini ketika tanda tangan atau token tidak cocok, atau ketika verifikasi sengaja dilewati."
          />
        )
      ) : (
        <section aria-labelledby="rej-heading">
          <div className="page-head">
            <div className="page-head__text">
              <h2 id="rej-heading">{formatCount(rows.length)} permintaan ditampilkan</h2>
            </div>
          </div>
          <div className="rows">
            {rows.map((row) => (
              <article className="row-item" key={row.id}>
                <div className="row-item__main">
                  <span className="row-item__title">
                    {row.operation} dari kredensial {row.credential}
                  </span>
                  <span className="row-item__meta">
                    {formatDateTimeWib(row.created_at)}, {row.method} {row.url}
                  </span>
                  <span className="row-item__meta">
                    <Badge tone={row.verification === 'ok' ? 'success' : 'warning'}>
                      {verificationLabel(row.verification)}
                    </Badge>
                    {` status balasan ${row.response_status ?? 'tanpa balasan'}`}
                    {row.error ? `, error ${row.error}` : ''}
                    {`, ${formatDuration(row.duration_ms)}`}
                  </span>
                  <JsonDisclosure summary="Header permintaan" value={row.request_headers} />
                  <JsonDisclosure summary="Isi permintaan" value={row.request_body} />
                  <JsonDisclosure summary="Isi balasan" value={row.response_body} />
                </div>
              </article>
            ))}
          </div>
          <div className="state__actions">
            {rejections.data?.next_cursor ? (
              <button
                type="button"
                className="btn"
                disabled={rejections.isFetching}
                onClick={() => {
                  setExtra(rows)
                  setCursor(rejections.data?.next_cursor)
                }}
              >
                {rejections.isFetching ? 'Memuat...' : 'Muat berikutnya'}
              </button>
            ) : (
              <p className="muted">Semua permintaan yang cocok sudah ditampilkan.</p>
            )}
          </div>
        </section>
      )}
    </>
  )
}

function mergeRows(previous: Exchange[], incoming: Exchange[]): Exchange[] {
  const seen = new Set(previous.map((row) => row.id))
  return [...previous, ...incoming.filter((row) => !seen.has(row.id))]
}
